import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  Observable,
  ReplaySubject,
  Subject,
  combineLatest,
  filter,
  finalize,
  map,
  switchMap,
  take,
} from 'rxjs';
import { ConfigStateService, PermissionService, RoutesService, eLayoutType } from '@abp/ng.core';
import { Confirmation, ConfirmationService, ToasterService } from '@abp/ng.theme.shared';
import { ProjectService } from 'src/app/projects/proxy/project.service';
import {
  GenerativeQuestionAnsweringTrainStatus,
  ProjectFlowsAndVirtualAgentsDto,
  ProjectFlowSummaryDto,
} from 'src/app/projects/proxy/project.models';
import { PROJECT_FLOWS_AND_VIRTUAL_AGENTS_KEY } from '../shared.consts';
import { getTenantUserNameStorageKey } from '../designer/utils';
import { CAI, ProjectVersionDataModel, RouteDataModel } from './cai-routes.model';
import { FlowCreateDto, FlowType, Flows } from '../designer/proxy/designer.model';
import {
  AI_AGENTS_PATH,
  AI_AGENT_ROOT_ROUTE_ID,
  AI_AGENT_ROOT_ROUTE_NAME,
  GUARDRAILS_PATH,
  GUARDRAIL_ROOT_ROUTE_ID,
  GUARDRAIL_ROOT_ROUTE_NAME,
  CONVERSATIONS_PATH,
  CONVERSATIONS_ROUTE_ID,
  CONVERSATIONS_ROUTE_NAME,
  CREATE_SUBFLOW_ROUTE_ID,
  CREATE_SUBFLOW_ROUTE_NAME,
  DASHBOARD_PATH,
  DASHBOARD_ROUTE_ID,
  DASHBOARD_ROUTE_NAME,
  EDIT_PATH,
  GENERATIVE_QUESTION_ANSWERING_RECOGNITIONS_PATH,
  GENERATIVE_QUESTION_ANSWERING_RECOGNITIONS_ROUTE_ID,
  GENERATIVE_QUESTION_ANSWERING_RECOGNITIONS_ROUTE_NAME,
  INSIGHTS_PREFIX_PATH,
  INSIGHTS_ROOT_ROUTE_ID,
  INSIGHTS_ROOT_ROUTE_NAME,
  INTENTS_PATH,
  KNOWLEDGE_BASES_PATH,
  NOTIFICATIONS_PATH,
  OUTBOUND_PATH,
  OUTBOUND_ROUTE_ID,
  OUTBOUND_ROUTE_NAME,
  KNOWLEDGE_BASE_RESPONSES_PATH,
  KNOWLEDGE_BASE_RESPONSES_ROUTE_ID,
  KNOWLEDGE_BASE_RESPONSES_ROUTE_NAME,
  PREMIUM_REPORTING_PATH,
  PREMIUM_REPORTING_ROUTE_NAME,
  PROJECTS_PATH,
  PROJECT_ROOT_ROUTE_ID,
  PROJECT_ROOT_ROUTE_NAME,
  PROMPTS_PREFIX_PATH,
  PROMPTS_ROOT_ROUTE_ID,
  PROMPTS_ROOT_ROUTE_NAME,
  RESPONSE_MANAGEMENT_PATH,
  RESPONSE_MANAGEMENT_ROUTE_ID,
  RESPONSE_MANAGEMENT_ROUTE_NAME,
  SCENARIO_DESIGN_PATH,
  SUBFLOW_TYPE_NAME,
  SYSTEM_MESSAGES_PATH,
  SYSTEM_MESSAGES_ROUTE_ID,
  SYSTEM_MESSAGES_ROUTE_NAME,
  VERSIONS_PATH,
  VIRTUAL_AGENTS_PATH,
  VIRTUAL_AGENTS_ROOT_ROUTE_NAME,
  VIRTUAL_AGENT_ROOT_ROUTE_ID,
  VIRTUAL_AGENT_ROOT_ROUTE_NAME,
  KNOWLEDGE_BASE_ROOT_ROUTE_ID,
  KNOWLEDGE_BASE_ROOT_ROUTE_NAME,
  AI_TESTING_PATH,
  AI_TESTING_ROOT_ROUTE_ID,
  AI_TESTING_ROOT_ROUTE_NAME,
  GENERAL_PATH,
} from './cai-routes.consts';
import { DesignerService } from '../designer/proxy/designer.service';
import { AI_FEATURE_GENERATIVE_QUESTION_ANSWERING } from 'src/app/virtual-agents/proxy/open-ai/open-ai.const';
import { OUTBOUND_FEATURE_OUTBOUND_CONVERSATIONS } from 'src/app/projects/proxy/outbound/outbound-rules.const';
import { CaiConfigService } from '../services/cai-config.service';
import { AGENTIC_AI_FEATURE } from 'src/app/ai-agents/proxy/ai-agents.consts';

@Injectable({ providedIn: 'root' })
export class CaiRoutesService {
  showSubflowUpdateForm$ = new Subject<ProjectFlowSummaryDto>();
  hideSubflowUpdateForm$ = new Subject<void>();
  updateSubFlow$ = new Subject<{ id: string; name: string }>();
  refreshProjectRoutes$ = new Subject<void>();

  isSubFlowSavingInProgress = false;

  private _routeData$ = new ReplaySubject<RouteDataModel>(1);
  private _routeData: RouteDataModel;
  get routeData(): RouteDataModel {
    return this._routeData;
  }
  private set routeData(value: RouteDataModel) {
    this._routeData = value;
    this._routeData$.next(value);
  }

  private _projectData$ = new ReplaySubject<ProjectVersionDataModel>(1);
  private _projectData: ProjectVersionDataModel;
  get projectData(): ProjectVersionDataModel {
    return this._projectData;
  }
  private set projectData(value: ProjectVersionDataModel) {
    value.isDataLoaded = !!value.projectId && !!value.projectVersionId && !!value.projectName;

    this._projectData = value;
    this._projectData$.next(value);
  }

  get freshProjectData$(): Observable<ProjectVersionDataModel> {
    return this._projectData$.pipe(filter((projectData) => projectData.isFreshData));
  }

  get freshProjectDataOnce$(): Observable<ProjectVersionDataModel> {
    return this.freshProjectData$.pipe(take(1));
  }

  get projectAndRouteData$(): Observable<ProjectVersionDataModel & RouteDataModel> {
    return combineLatest([this.freshProjectData$, this._routeData$]).pipe(
      map(([projectData, routeData]) => ({ ...projectData, ...routeData })),
    );
  }

  get isProjectMenuOpened(): boolean {
    return (
      // Is project edit menu
      this.isProjectEditMenuOpened ||
      // Is project flow menu
      this.isProjectFlowsMenuOpened ||
      // Is virtual agent menu
      this.isVirtualAgentMenuOpened ||
      // Is ai agent menu
      this.isAiAgentMenuOpened ||
      // Is guardrail menu
      this.isGuardrailMenuOpened ||
      // Is knowledge base menu
      this.isKnowledgeBaseMenuOpened ||
      // Is prompts menu
      this.isPromptsMenuOpened ||
      // Is other project menu
      this.currentUrlSegments[2] === AI_TESTING_PATH ||
      this.currentUrlSegments[2] === DASHBOARD_PATH ||
      this.currentUrlSegments[2] === INSIGHTS_PREFIX_PATH ||
      this.currentUrlSegments[2] === PREMIUM_REPORTING_PATH ||
      this.currentUrlSegments[2] === NOTIFICATIONS_PATH
    );
  }

  private get isProjectEditMenuOpened(): boolean {
    return this.currentUrlSegments[0] === PROJECTS_PATH && this.currentUrlSegments[4] === EDIT_PATH;
  }

  private get isProjectVirtualAgentEditMenuOpened(): boolean {
    return this.isProjectEditMenuOpened && this.currentUrlSegments[5] === VIRTUAL_AGENTS_PATH;
  }

  private get isProjectFlowsMenuOpened(): boolean {
    return (
      this.currentUrlSegments[0] === PROJECTS_PATH &&
      this.currentUrlSegments[2] === VERSIONS_PATH &&
      this.currentUrlSegments[4]?.startsWith(SCENARIO_DESIGN_PATH) &&
      this.currentUrlSegments.length >= this.flowDesignerPageUrlSegmentsLength
    );
  }

  private get isVirtualAgentMenuOpened(): boolean {
    return (
      this.currentUrlSegments[0] === PROJECTS_PATH &&
      this.currentUrlSegments[4] === VIRTUAL_AGENTS_PATH &&
      this.currentUrlSegments.length >= this.virtualAgentDetailPageUrlSegmentsLength
    );
  }

  private get isAiAgentMenuOpened(): boolean {
    return (
      this.currentUrlSegments[0] === PROJECTS_PATH && this.currentUrlSegments[4] === AI_AGENTS_PATH
    );
  }

  private get isGuardrailMenuOpened(): boolean {
    return (
      this.currentUrlSegments[0] === PROJECTS_PATH && this.currentUrlSegments[4] === GUARDRAILS_PATH
    );
  }

  private get isKnowledgeBaseMenuOpened(): boolean {
    return (
      this.currentUrlSegments[0] === PROJECTS_PATH &&
      this.currentUrlSegments[4] === KNOWLEDGE_BASES_PATH
    );
  }

  private get isPromptsMenuOpened(): boolean {
    return (
      this.currentUrlSegments[0] === PROJECTS_PATH &&
      this.currentUrlSegments[4] === PROMPTS_PREFIX_PATH
    );
  }

  private get premiumReportingRoutes() {
    return this.routesService.flat.filter(this.isPremiumReportingRoute);
  }

  private currentUrlSegments: string[] = [];
  private virtualAgentDetailPageUrlSegmentsLength = 6;
  private flowDesignerPageUrlSegmentsLength = 5;

  constructor(
    private router: Router,
    private toasterService: ToasterService,
    private routesService: RoutesService,
    private configStateService: ConfigStateService,
    private confirmationService: ConfirmationService,
    private projectService: ProjectService,
    private permissionService: PermissionService,
    private designerService: DesignerService,
    private caiConfigService: CaiConfigService,
  ) {
    this.updateProjectData('', '');

    this.updateSubFlow$.subscribe((flow) => this.updateSubFlow(flow));

    this.refreshProjectRoutes$.subscribe(() =>
      this.refreshProjectRoutes(this.projectData.projectId, this.projectData.projectVersionId),
    );

    this.routesService.flat$.subscribe(() => {
      this.premiumReportingRoutes.forEach((route) => {
        if (this.projectData.projectId) {
          const urlSegments = route.path.split('/').filter((s) => s);

          if (urlSegments[2] === PREMIUM_REPORTING_PATH) {
            urlSegments.shift();
            urlSegments.shift();
          }

          urlSegments.unshift('/projects', this.projectData.projectId);

          route.path = urlSegments.join('/');
        }
      });
    });

    this.refreshRoute();
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.refreshRoute();
      }
    });
  }

  isPremiumReportingRoute(route: CAI.Route) {
    return (
      PREMIUM_REPORTING_ROUTE_NAME === route.name ||
      PREMIUM_REPORTING_ROUTE_NAME === route.parentName
    );
  }

  private refreshRoute() {
    const navigation = this.router.getCurrentNavigation();
    const projectData: ProjectVersionDataModel = navigation?.extras?.state?.projectData;

    this.currentUrlSegments = this.router.url.split('/').filter((element) => element);

    if (this.isProjectMenuOpened) {
      const projectId = this.currentUrlSegments[1];
      const projectVersionId =
        this.currentUrlSegments[2] === VERSIONS_PATH ? this.currentUrlSegments[3] : '';
      const isProjectChanged = this.projectData.projectId !== projectId;
      const isProjectVersionChanged =
        projectVersionId && this.projectData.projectVersionId !== projectVersionId;

      if (isProjectChanged || isProjectVersionChanged) {
        if (projectData) {
          this.projectData = projectData;
          this.cacheProjectFlowsAndVirtualAgents(this.projectData);
          this.findAndSetRouteData();
          this.replaceProjectRoutes();
        } else {
          this.updateProjectData(projectId, projectVersionId);
          this.replaceProjectRoutes(); // Replace old projects routes with new ones from cache
          this.refreshProjectRoutes(projectId, projectVersionId); // Load new projects routes
        }
      } else {
        this.findAndSetRouteData();
      }
    } else {
      this.updateProjectData('', '');
      this.findAndSetRouteData();
      this.removeProjectRoutes();
    }

    this.routesService.refresh();
  }

  private updateProjectData(projectId: string, projectVersionId: string) {
    if (
      projectId !== this.projectData?.projectId ||
      projectVersionId !== this.projectData?.projectVersionId
    ) {
      this.projectData = this.getCachedProjectFlowsAndVirtualAgents(projectId, projectVersionId);
    }
  }

  private findAndSetRouteData(forceToSet: boolean = false) {
    const routeData: RouteDataModel = {};

    if (this.isProjectVirtualAgentEditMenuOpened) {
      routeData.virtualAgentId = this.currentUrlSegments[6];
    } else if (this.isProjectFlowsMenuOpened) {
      const flowId = this.currentUrlSegments[5];
      const currentFlow = this.projectData.flowSummaries.find(
        (flow) => flow.id === flowId || (!flowId && flow.flowType === FlowType.main),
      );

      if (currentFlow) {
        routeData.flowId = currentFlow.id;
        routeData.flowName = currentFlow.name;
      }
    } else if (this.isVirtualAgentMenuOpened) {
      routeData.virtualAgentId = this.currentUrlSegments[5];
      routeData.virtualAgentName = this.currentUrlSegments[6];
      routeData.intentId = this.currentUrlSegments[8];
    } else if (this.isAiAgentMenuOpened) {
      routeData.aiAgentId = this.currentUrlSegments[5];
    } else if (this.isGuardrailMenuOpened && this.currentUrlSegments.length > 5) {
      routeData.guardrailId = this.currentUrlSegments[5];
    } else if (this.isKnowledgeBaseMenuOpened && this.currentUrlSegments.length > 6) {
      routeData.knowledgeBaseId = this.currentUrlSegments[5];
    }

    if (forceToSet || JSON.stringify(routeData) !== JSON.stringify(this.routeData)) {
      this.routeData = routeData;
    }
  }

  private replaceProjectRoutes() {
    this.removeProjectRoutes();
    this.routesService.add(this.buildProjectRoutes());
  }

  private refreshProjectRoutes(
    projectId: string,
    projectVersionId: string,
    previousVersionData?: ProjectVersionDataModel,
    previousRouteData?: RouteDataModel,
  ) {
    this.projectService.getProjectFlowsAndVirtualAgents(projectId, projectVersionId).subscribe({
      next: (result) => {
        if (previousRouteData) {
          const commands = this.getCommandsForVersionSwitch(
            result,
            previousVersionData,
            previousRouteData,
          );

          if (commands) {
            // Navigate away first to force Angular to destroy and recreate the component,
            // since router.navigate to the same route pattern only updates params
            // without re-instantiating the component (take(1) subscriptions won't re-fire).
            this.router.navigateByUrl('/', { skipLocationChange: true }).then(() => {
              this.router.navigate(commands, {
                state: {
                  projectData: { ...result, isDataLoaded: true, isFreshData: true },
                },
              });
            });

            return;
          }
          // For non-version-dependent pages (dashboard, insights, etc.):
          // URLs don't include versionId, components react to currentProjectRouteData$ changes
        }

        this.projectData = { ...result, isDataLoaded: true, isFreshData: true };
        this.cacheProjectFlowsAndVirtualAgents(this.projectData);
        this.findAndSetRouteData();
        this.replaceProjectRoutes();
      },
    });
  }

  switchToVersion(isPublished: boolean) {
    if (this.projectData.isPublishedVersion !== isPublished) {
      const targetVersionId = isPublished
        ? this.projectData.publishedProjectVersionId
        : this.projectData.draftProjectVersionId;
      const previousRouteData = { ...this.routeData };
      const previousProjectData = { ...this.projectData };

      this.refreshProjectRoutes(
        this.projectData.projectId,
        targetVersionId,
        previousProjectData,
        previousRouteData,
      );
    }
  }

  private getCommandsForVersionSwitch(
    newVersionData: ProjectFlowsAndVirtualAgentsDto,
    previousVersionData: ProjectVersionDataModel,
    previousRouteData: RouteDataModel,
  ) {
    const projectId = newVersionData.projectId;
    const versionId = newVersionData.projectVersionId;

    let targetCommands: string[];

    if (this.isProjectEditMenuOpened) {
      if (this.isProjectVirtualAgentEditMenuOpened) {
        const matchingVA = previousVersionData.virtualAgentSummaries.find(
          (va) => va.id === previousRouteData.virtualAgentId,
        );
        const targetVA = newVersionData.virtualAgentSummaries.find(
          (f) => f.name === matchingVA?.name,
        );

        targetCommands = [
          PROJECTS_PATH,
          projectId,
          VERSIONS_PATH,
          versionId,
          EDIT_PATH,
          ...(targetVA ? [VIRTUAL_AGENTS_PATH, targetVA.id] : [GENERAL_PATH]),
        ];
      } else {
        // Stay on the same edit sub-page with new versionId
        targetCommands = [
          PROJECTS_PATH,
          projectId,
          VERSIONS_PATH,
          versionId,
          EDIT_PATH,
          ...this.currentUrlSegments.slice(5).map((segment) => segment.split('?')[0]),
        ];
      }
    } else if (this.isProjectFlowsMenuOpened) {
      // Match flow by name, fallback to main flow
      const matchingFlow = previousRouteData.flowName
        ? newVersionData.flowSummaries.find((f) => f.name === previousRouteData.flowName)
        : null;
      const targetFlow =
        matchingFlow ?? newVersionData.flowSummaries.find((f) => f.flowType === FlowType.main);

      targetCommands = [
        PROJECTS_PATH,
        projectId,
        VERSIONS_PATH,
        versionId,
        SCENARIO_DESIGN_PATH,
        targetFlow.id,
      ];
    } else if (this.isVirtualAgentMenuOpened) {
      // Match virtual agent by name, fallback to first virtual agent
      const matchingVA = previousRouteData.virtualAgentName
        ? newVersionData.virtualAgentSummaries.find(
            (va) => va.name === previousRouteData.virtualAgentName,
          )
        : null;
      const targetVA = matchingVA ?? newVersionData.virtualAgentSummaries[0];

      targetCommands = [
        PROJECTS_PATH,
        projectId,
        VERSIONS_PATH,
        versionId,
        VIRTUAL_AGENTS_PATH,
        targetVA.id,
        targetVA.name,
        INTENTS_PATH,
      ];
    } else if (this.isAiAgentMenuOpened) {
      targetCommands = [PROJECTS_PATH, projectId, VERSIONS_PATH, versionId, AI_AGENTS_PATH];
    } else if (this.isGuardrailMenuOpened) {
      targetCommands = [PROJECTS_PATH, projectId, VERSIONS_PATH, versionId, GUARDRAILS_PATH];
    } else if (this.isKnowledgeBaseMenuOpened) {
      targetCommands = [PROJECTS_PATH, projectId, VERSIONS_PATH, versionId, KNOWLEDGE_BASES_PATH];
    } else if (this.isPromptsMenuOpened) {
      // Stay on the same prompts sub-page with new versionId
      targetCommands = [
        PROJECTS_PATH,
        projectId,
        VERSIONS_PATH,
        versionId,
        PROMPTS_PREFIX_PATH,
        ...this.currentUrlSegments.slice(5),
      ];
    }

    return targetCommands;
  }

  private removeProjectRoutes() {
    const routesToBeRemoved = this.routesService.flat
      .filter((route: CAI.Route) => route.isProjectRoute)
      .map((route) => route.name);

    this.routesService.remove(routesToBeRemoved);
  }

  private buildProjectRoutes(): CAI.Route[] {
    const projectId = this.projectData.projectId;
    let projectVersionId = this.projectData.projectVersionId;
    const cachedValue = this.getCachedProjectFlowsAndVirtualAgents(projectId, projectVersionId);

    const isAgenticAiFeatureEnabled = this.caiConfigService.isFeatureEnabled(AGENTIC_AI_FEATURE);
    const showGenerativeQuestionAnsweringMenuItem =
      this.projectData.generativeQuestionAnsweringTrainStatus !==
        GenerativeQuestionAnsweringTrainStatus.deprecated &&
      this.caiConfigService.isFeatureEnabled(AI_FEATURE_GENERATIVE_QUESTION_ANSWERING);
    const isOutboundConversationsFeatureEnabled = this.caiConfigService.isFeatureEnabled(
      OUTBOUND_FEATURE_OUTBOUND_CONVERSATIONS,
    );
    const showConversationsMenu = this.permissionService.getGrantedPolicy('Designer.Conversations');

    const projectRootRoute: CAI.Route = {
      id: PROJECT_ROOT_ROUTE_ID,
      name: PROJECT_ROOT_ROUTE_NAME,
      layout: eLayoutType.application,
      iconClass: 'fa-sharp fa-regular fa-diagram-project',
      order: 0,
      requiredPolicy: 'Designer.Projects',
      isProjectRoute: true,
    };
    const virtualAgentRootRoute: CAI.Route = {
      id: VIRTUAL_AGENT_ROOT_ROUTE_ID,
      name: VIRTUAL_AGENT_ROOT_ROUTE_NAME,
      layout: eLayoutType.application,
      iconClass: 'fa-solid fa-users',
      order: 1,
      requiredPolicy: 'Designer.Projects',
      isProjectRoute: true,
    };
    const routes: CAI.Route[] = [projectRootRoute, virtualAgentRootRoute];

    routes.push(
      {
        id: PROMPTS_ROOT_ROUTE_ID,
        name: PROMPTS_ROOT_ROUTE_NAME,
        layout: eLayoutType.application,
        iconClass: 'fa-sharp fa-regular fa-message-pen',
        order: 4,
        requiredPolicy: 'Designer.Projects',
        isProjectRoute: true,
      },
      {
        id: DASHBOARD_ROUTE_ID,
        name: DASHBOARD_ROUTE_NAME,
        path: `${PROJECTS_PATH}/${projectId}/${DASHBOARD_PATH}`,
        iconClass: 'fa-solid fa-chart-line',
        layout: eLayoutType.application,
        order: 5,
        requiredPolicy: 'Designer.Dashboard.Tenant',
        isProjectRoute: true,
      },
    );

    if (
      showConversationsMenu ||
      isAgenticAiFeatureEnabled ||
      showGenerativeQuestionAnsweringMenuItem ||
      isOutboundConversationsFeatureEnabled
    ) {
      routes.push({
        id: INSIGHTS_ROOT_ROUTE_ID,
        name: INSIGHTS_ROOT_ROUTE_NAME,
        layout: eLayoutType.application,
        iconClass: 'fa-solid fa-messages',
        order: 6,
        isProjectRoute: true,
      });

      if (showConversationsMenu) {
        routes.push({
          id: CONVERSATIONS_ROUTE_ID,
          name: CONVERSATIONS_ROUTE_NAME,
          path: `${PROJECTS_PATH}/${projectId}/${INSIGHTS_PREFIX_PATH}/${CONVERSATIONS_PATH}`,
          layout: eLayoutType.application,
          order: 61,
          requiredPolicy: 'Designer.Conversations',
          parentName: INSIGHTS_ROOT_ROUTE_NAME,
          isProjectRoute: true,
        });
      }

      if (isAgenticAiFeatureEnabled) {
        routes.push({
          id: KNOWLEDGE_BASE_RESPONSES_ROUTE_ID,
          name: KNOWLEDGE_BASE_RESPONSES_ROUTE_NAME,
          path: `${PROJECTS_PATH}/${projectId}/${INSIGHTS_PREFIX_PATH}/${KNOWLEDGE_BASE_RESPONSES_PATH}`,
          layout: eLayoutType.application,
          order: 62,
          requiredPolicy: 'Designer.KnowledgeBaseReporting',
          parentName: INSIGHTS_ROOT_ROUTE_NAME,
          isProjectRoute: true,
        });
      }

      if (showGenerativeQuestionAnsweringMenuItem) {
        routes.push({
          id: GENERATIVE_QUESTION_ANSWERING_RECOGNITIONS_ROUTE_ID,
          name: GENERATIVE_QUESTION_ANSWERING_RECOGNITIONS_ROUTE_NAME,
          path: `${PROJECTS_PATH}/${projectId}/${INSIGHTS_PREFIX_PATH}/${GENERATIVE_QUESTION_ANSWERING_RECOGNITIONS_PATH}`,
          layout: eLayoutType.application,
          order: 63,
          requiredPolicy: 'Designer.GenerativeQuestionAnsweringReporting',
          parentName: INSIGHTS_ROOT_ROUTE_NAME,
          isProjectRoute: true,
        });
      }

      if (isOutboundConversationsFeatureEnabled) {
        routes.push({
          id: OUTBOUND_ROUTE_ID,
          name: OUTBOUND_ROUTE_NAME,
          path: `${PROJECTS_PATH}/${projectId}/${INSIGHTS_PREFIX_PATH}/${OUTBOUND_PATH}`,
          layout: eLayoutType.application,
          order: 64,
          requiredPolicy: 'Designer.OutboundReporting',
          parentName: INSIGHTS_ROOT_ROUTE_NAME,
          isProjectRoute: true,
          invisible: !(cachedValue.isOutboundProject ?? false),
        });
      }
    }

    if (cachedValue.isDataLoaded) {
      projectVersionId = cachedValue.projectVersionId;

      routes.push(
        {
          id: RESPONSE_MANAGEMENT_ROUTE_ID,
          name: RESPONSE_MANAGEMENT_ROUTE_NAME,
          path: `${PROJECTS_PATH}/${projectId}/${VERSIONS_PATH}/${projectVersionId}/${RESPONSE_MANAGEMENT_PATH}`,
          layout: eLayoutType.application,
          order: 41,
          requiredPolicy: 'Designer.Projects',
          parentName: PROMPTS_ROOT_ROUTE_NAME,
          isProjectRoute: true,
        },
        {
          id: SYSTEM_MESSAGES_ROUTE_ID,
          name: SYSTEM_MESSAGES_ROUTE_NAME,
          path: `${PROJECTS_PATH}/${projectId}/${VERSIONS_PATH}/${projectVersionId}/${SYSTEM_MESSAGES_PATH}`,
          layout: eLayoutType.application,
          order: 42,
          requiredPolicy: 'Designer.Projects',
          parentName: PROMPTS_ROOT_ROUTE_NAME,
          isProjectRoute: true,
        },
      );

      if (isAgenticAiFeatureEnabled) {
        routes.push(
          {
            id: AI_AGENT_ROOT_ROUTE_ID,
            name: AI_AGENT_ROOT_ROUTE_NAME,
            path: `${PROJECTS_PATH}/${projectId}/${VERSIONS_PATH}/${projectVersionId}/${AI_AGENTS_PATH}`,
            layout: eLayoutType.application,
            iconClass: 'fa-regular fa-microchip-ai',
            order: 2,
            requiredPolicy: 'Designer.AiAgents',
            isProjectRoute: true,
          },
          {
            id: AI_TESTING_ROOT_ROUTE_ID,
            name: AI_TESTING_ROOT_ROUTE_NAME,
            path: `${PROJECTS_PATH}/${projectId}/${AI_TESTING_PATH}`,
            layout: eLayoutType.application,
            iconClass: 'fa-regular fa-flask-vial',
            order: 3,
            requiredPolicy: 'Designer.AiTesting',
            isProjectRoute: true,
          },
          {
            id: GUARDRAIL_ROOT_ROUTE_ID,
            name: GUARDRAIL_ROOT_ROUTE_NAME,
            path: `${PROJECTS_PATH}/${projectId}/${VERSIONS_PATH}/${projectVersionId}/${GUARDRAILS_PATH}`,
            layout: eLayoutType.application,
            iconClass: 'fa-regular fa-shield-check',
            order: 4,
            requiredPolicy: 'Designer.Guardrails',
            isProjectRoute: true,
          },
          {
            id: KNOWLEDGE_BASE_ROOT_ROUTE_ID,
            name: KNOWLEDGE_BASE_ROOT_ROUTE_NAME,
            path: `${PROJECTS_PATH}/${projectId}/${VERSIONS_PATH}/${projectVersionId}/${KNOWLEDGE_BASES_PATH}`,
            layout: eLayoutType.application,
            iconClass: 'fa-solid fa-layer-plus',
            order: 5,
            requiredPolicy: 'Designer.Projects',
            isProjectRoute: true,
          },
        );
      }

      cachedValue.flowSummaries.forEach((flow, order) => {
        const projectFlowRoute = this.buildProjectFlowRoute(
          flow,
          projectId,
          projectVersionId,
          cachedValue.isPublishedVersion,
          projectRootRoute.name,
          order,
        );

        routes.push(projectFlowRoute);
      });

      if (!cachedValue.isPublishedVersion) {
        const createSubFlowRoute: CAI.Route = {
          id: CREATE_SUBFLOW_ROUTE_ID,
          name: CREATE_SUBFLOW_ROUTE_NAME,
          layout: eLayoutType.application,
          parentName: projectRootRoute.name,
          order: 1000,
          requiredPolicy: 'Designer.Projects.Edit',
          onClick: () => this.createSubFlow(),
          isProjectRoute: true,
        };

        routes.push(createSubFlowRoute);
      }

      if (cachedValue.virtualAgentSummaries.length === 1) {
        const virtualAgent = cachedValue.virtualAgentSummaries[0];

        virtualAgentRootRoute.id = virtualAgent.id;
        virtualAgentRootRoute.path = `${PROJECTS_PATH}/${projectId}/${VERSIONS_PATH}/${projectVersionId}/${VIRTUAL_AGENTS_PATH}/${virtualAgent.id}/${virtualAgent.name}/${INTENTS_PATH}`;
      } else {
        virtualAgentRootRoute.name = VIRTUAL_AGENTS_ROOT_ROUTE_NAME;

        cachedValue.virtualAgentSummaries.forEach((virtualAgent, order) => {
          const virtualAgentRoute: CAI.Route = {
            id: virtualAgent.id,
            name: virtualAgent.name,
            path: `${PROJECTS_PATH}/${projectId}/${VERSIONS_PATH}/${projectVersionId}/${VIRTUAL_AGENTS_PATH}/${virtualAgent.id}/${virtualAgent.name}/${INTENTS_PATH}`,
            layout: eLayoutType.application,
            parentName: virtualAgentRootRoute.name,
            order,
            requiredPolicy: 'Designer.Projects',
            isProjectRoute: true,
          };

          routes.push(virtualAgentRoute);
        });
      }
    }

    return routes;
  }

  private buildProjectFlowRoute(
    flow: { id: string; name: string; flowType: FlowType },
    projectId: string,
    projectVersionId: string,
    isPublishedVersion: boolean,
    parentName: string,
    order: number,
  ): CAI.Route {
    let routeActions: CAI.RouteAction[] = [];

    if (flow.flowType === FlowType.subFlow && !isPublishedVersion) {
      routeActions = [
        {
          name: '::Rename',
          iconClass: 'fa-solid fa-edit text-secondary',
          requiredPolicy: 'Designer.Projects.Edit',
          onClick: () => {
            const updatedFlow = this.projectData.flowSummaries.find((f) => f.id === flow.id);
            this.showSubflowUpdateForm$.next(updatedFlow);
          },
        },
        {
          name: 'AbpUi::Delete',
          iconClass: 'fa-solid fa-trash text-secondary',
          requiredPolicy: 'Designer.Projects.Edit',
          onClick: () => {
            const updatedFlow = this.projectData.flowSummaries.find((f) => f.id === flow.id);

            this.deleteSubFlow(updatedFlow);
          },
        },
      ];
    }

    return {
      id: flow.id,
      name: flow.name,
      path: `${PROJECTS_PATH}/${projectId}/${VERSIONS_PATH}/${projectVersionId}/${SCENARIO_DESIGN_PATH}/${flow.id}`,
      layout: eLayoutType.application,
      parentName: parentName,
      order,
      requiredPolicy: 'Designer.Projects',
      actions: routeActions,
      isProjectRoute: true,
    };
  }

  private getCachedProjectFlowsAndVirtualAgents(
    projectId: string,
    projectVersionId: string,
  ): ProjectVersionDataModel {
    const storageKey = getTenantUserNameStorageKey(
      this.configStateService,
      PROJECT_FLOWS_AND_VIRTUAL_AGENTS_KEY,
    );
    const cachedValuesStr = localStorage.getItem(storageKey);
    const cachedValues: Record<string, ProjectVersionDataModel> = cachedValuesStr
      ? JSON.parse(cachedValuesStr)
      : {};
    let cachedValue: ProjectVersionDataModel;

    if (projectVersionId) {
      cachedValue = cachedValues['PV-' + projectVersionId];
    } else if (projectId) {
      cachedValue = cachedValues['P-' + projectId];
    }

    return cachedValue?.isDataLoaded
      ? { ...cachedValue, isFreshData: false }
      : {
          projectId,
          projectName: '',
          projectVersionId,
          draftProjectVersionId: '',
          publishedProjectVersionId: '',
          isPublishedVersion: false,
          hasPublishedVersion: false,
          isOutboundProject: false,
          areAiInsightsEnabled: false,
          generativeQuestionAnsweringTrainStatus: GenerativeQuestionAnsweringTrainStatus.deprecated,
          hasSRIntegration: false,
          hasTTSIntegration: false,
          version: null,
          lastModificationTime: null,
          publishedDate: null,
          flowSummaries: [],
          virtualAgentSummaries: [],
          isDataLoaded: false,
          isFreshData: !projectId,
        };
  }

  private cacheProjectFlowsAndVirtualAgents(value: ProjectVersionDataModel) {
    const storageKey = getTenantUserNameStorageKey(
      this.configStateService,
      PROJECT_FLOWS_AND_VIRTUAL_AGENTS_KEY,
    );
    let cachedValuesStr = localStorage.getItem(storageKey);
    let cachedValues = new Map<string, ProjectVersionDataModel>();
    if (cachedValuesStr) {
      cachedValues = JSON.parse(cachedValuesStr);
    }

    cachedValues['PV-' + value.projectVersionId] = value;
    cachedValues['P-' + value.projectId] = value;
    cachedValuesStr = JSON.stringify(cachedValues);

    localStorage.setItem(storageKey, cachedValuesStr);
  }

  private createSubFlow() {
    this.isSubFlowSavingInProgress = true;

    const subFlowCount = this.projectData.flowSummaries.filter(
      (flow) => flow.flowType === FlowType.subFlow,
    ).length;
    const subflowName = this.generateSubFlowName(subFlowCount + 1);

    const flowCreateDto: FlowCreateDto = {
      projectVersionId: this.projectData.projectVersionId,
      name: subflowName,
      flowType: FlowType.subFlow,
    };

    this.designerService
      .createSubFlow(flowCreateDto)
      .pipe(finalize(() => (this.isSubFlowSavingInProgress = false)))
      .subscribe({
        next: (flow: Flows) => {
          this.toasterService.success('AbpUi::SavedSuccessfully');

          const parentName = this.routesService.flat.find(
            (route) => route.name === PROJECT_ROOT_ROUTE_NAME,
          ).name;
          const order =
            Math.max(
              ...this.routesService.flat
                .filter(
                  (route: CAI.Route) =>
                    route.parentName === parentName && route.id !== CREATE_SUBFLOW_ROUTE_ID,
                )
                .map((route) => route.order),
            ) + 1;

          const projectFlowRoute = this.buildProjectFlowRoute(
            flow,
            this.projectData.projectId,
            this.projectData.projectVersionId,
            this.projectData.isPublishedVersion,
            parentName,
            order,
          );

          this.routesService.add([projectFlowRoute]);
          this.projectData.flowSummaries.push({
            id: flow.id,
            name: flow.name,
            reducedName: flow.name,
            flowType: flow.flowType,
          });
          this.cacheProjectFlowsAndVirtualAgents(this.projectData);

          this.router.navigate([
            PROJECTS_PATH,
            this.projectData.projectId,
            VERSIONS_PATH,
            this.projectData.projectVersionId,
            SCENARIO_DESIGN_PATH,
            flow.id,
          ]);
        },
      });
  }

  private generateSubFlowName(currentNumber: number) {
    const generatedName = SUBFLOW_TYPE_NAME + ' ' + currentNumber;

    const isExist = this.projectData.flowSummaries.some((flow) => flow.name === generatedName);

    return isExist ? this.generateSubFlowName(currentNumber + 1) : generatedName;
  }

  private updateSubFlow(flow: { id: string; name: string }) {
    this.isSubFlowSavingInProgress = true;

    this.designerService
      .updateSubFlowName(flow.id, { name: flow.name })
      .pipe(finalize(() => (this.isSubFlowSavingInProgress = false)))
      .subscribe({
        next: (updatedFlow: Flows) => {
          this.toasterService.success('AbpUi::SavedSuccessfully');

          this.hideSubflowUpdateForm$.next();

          this.projectData.flowSummaries.find((f) => f.id === updatedFlow.id).name =
            updatedFlow.name;

          this.cacheProjectFlowsAndVirtualAgents(this.projectData);

          const updatedRoute = this.routesService.flat.find(
            (route: CAI.Route) => route.id === updatedFlow.id,
          );

          this.routesService.patch(updatedRoute.name, { name: updatedFlow.name });
          this.findAndSetRouteData(true);
        },
      });
  }

  private deleteSubFlow(flow: ProjectFlowSummaryDto) {
    this.confirmationService
      .warn('::DeleteConfirmationMessageWithName', '::DeleteRecordConfirmationMessage', {
        messageLocalizationParams: [flow.name],
      })
      .pipe(
        filter((status) => status === Confirmation.Status.confirm),
        switchMap(() => this.designerService.deleteSubFlow(flow.id)),
      )
      .subscribe(() => {
        const deletedRoute = this.routesService.flat.find((route) => route.name === flow.name);
        this.routesService.remove([flow.name]);
        this.projectData.flowSummaries = this.projectData.flowSummaries.filter(
          (f) => f.id !== flow.id,
        );
        this.cacheProjectFlowsAndVirtualAgents(this.projectData);

        // Check if the current sub flow is deleted
        if (this.isProjectFlowsMenuOpened && this.currentUrlSegments[5] === flow.id) {
          const previousRoute = this.routesService.flat
            .filter(
              (route) =>
                route.parentName === deletedRoute.parentName && route.order < deletedRoute.order,
            )
            .pop();
          const previousFlow = this.projectData.flowSummaries.find(
            (f) => f.name === previousRoute.name,
          );

          this.router.navigate([
            PROJECTS_PATH,
            this.projectData.projectId,
            VERSIONS_PATH,
            this.projectData.projectVersionId,
            SCENARIO_DESIGN_PATH,
            previousFlow.id,
          ]);
        }
      });
  }
}
